import {APIService} from "./api.service";
import {map, Observable} from "rxjs";
import {IGameInstanceSummaryReport} from "../interfaces/IGameInstanceSummaryReport";
import {Injectable} from "@angular/core";
import {IGetRaffleReportResult} from "../interfaces/reporting/IGetRaffleReportResult";
import {IPlayersAndTicketsPerGameInstance} from "../interfaces/reporting/IPlayersAndTicketsPerGameInstance";
import {IPlayerRetentionReportResult} from "../interfaces/reporting/IPlayerRetentionReportResult";
import {IHourlyTicketSalesPerHourPerGame} from "../interfaces/reporting/IHourlyTicketSalesPerHourPerGame";
import {IManualAndAutoPlayTickets} from "../interfaces/reporting/IManualAndAutoPlayTickets";
import {ISalesPerDay} from "../interfaces/reporting/ISalesPerDay";
import {ICharitySalesPerDraw} from "../interfaces/reporting/ICharitySalesPerDraw";
import {IComprehensiveReport} from "../interfaces/reporting/IComprehensiveReport";
import {ILotteryLicenseReport} from "../interfaces/reporting/ILotteryLicenseReport";
import {IGameCharitySupporterBreakdown} from "../interfaces/reporting/IGameCharitySupporterBreakdown";
import {IGameInstanceShared} from "../interfaces/IGameInstanceShared";
import {IPlayedAndNotPlayedPerInstance} from "../interfaces/reporting/IPlayedAndNotPlayedPerInstance";
import {IPlayersPerDateRange} from "../interfaces/reporting/IPlayersPerDateRange";
import {IStandardGameReportPerDateRange} from "../interfaces/IStandardGameReportPerDateRange";
import {reportDay} from "../shared/report-day";
import {HttpParams} from "@angular/common/http";
import {IPayoutReportParams} from "../interfaces/reporting/IPayoutReportParams";
import {IPayoutReportResponse} from "../interfaces/reporting/IPayoutReportResponse";
import {IDynamicReportParams, IMultiGameDynamicReportParams} from "../interfaces/reporting/IDynamicReportParams";
import {IDynamicReportResponse} from "../interfaces/reporting/IDynamicReportResponse";
import {IMultiGameDynamicReportResponse} from "../interfaces/reporting/IMultiGameDynamicReportResponse";

@Injectable({
  providedIn: 'root'
})
export class ReportingService {
  constructor(private apiService: APIService) {
  }

  // Every flag has to be listed here explicitly — a param missing from this map is silently dropped on
  // the way to the API, and the report then renders as though the field had never been selected. Shared by the
  // single-game and multi-game calls so the two cannot drift.
  private dynamicReportFields(paramsP: IDynamicReportParams | IMultiGameDynamicReportParams): HttpParams {
    let queryParams = new HttpParams()
      .set('dateSelectionType', paramsP.dateSelectionType)
      .set('includeGrossSales', paramsP.includeGrossSales)
      .set('includeCausableFees', paramsP.includeCausableFees)
      .set('includeStripeFees', paramsP.includeStripeFees)
      .set('includeDisputeFees', paramsP.includeDisputeFees)
      .set('includePrizeAmount', paramsP.includePrizeAmount)
      .set('includeNetCharityContribution', paramsP.includeNetCharityContribution)
      .set('includeAdminFees', paramsP.includeAdminFees)
      .set('includeRefundFees', paramsP.includeRefundFees)
      .set('includeDrawCounts', paramsP.includeDrawCounts)
      .set('includeTicketCounts', paramsP.includeTicketCounts)
      .set('includePlayerCounts', paramsP.includePlayerCounts)
      .set('includeCharityBreakdown', paramsP.includeCharityBreakdown)
      .set('includeCharityBalances', paramsP.includeCharityBalances ?? false)
      .set('includeLicenceFigures', paramsP.includeLicenceFigures)
      .set('includePrizeWinners', paramsP.includePrizeWinners);

    if (paramsP.startDate) queryParams = queryParams.set('startDate', reportDay(paramsP.startDate));
    if (paramsP.endDate) queryParams = queryParams.set('endDate', reportDay(paramsP.endDate));

    return queryParams;
  }

  public getDynamicReport(paramsP: IDynamicReportParams): Observable<IDynamicReportResponse> {
    let queryParams = this.dynamicReportFields(paramsP)
      .set('gameId', paramsP.gameId)
      .set('includeHostPayoutRules', paramsP.includeHostPayoutRules ?? false);

    if (paramsP.gameInstanceId) queryParams = queryParams.set('gameInstanceId', paramsP.gameInstanceId);
    if (paramsP.includeInstanceBreakdown) queryParams = queryParams.set('includeInstanceBreakdown', true);

    return this.apiService.MakeGetRequest<IDynamicReportResponse>('report/dynamic-report', queryParams);
  }

  public getMultiGameDynamicReport(paramsP: IMultiGameDynamicReportParams): Observable<IMultiGameDynamicReportResponse> {
    let queryParams = this.dynamicReportFields(paramsP);

    // Repeated GameIds= entries: the model binder reads a list from them, and one game selected still binds as a list.
    for (const game_id of paramsP.gameIds) {
      queryParams = queryParams.append('gameIds', game_id);
    }

    return this.apiService.MakeGetRequest<IMultiGameDynamicReportResponse>('report/dynamic-report/multi-game', queryParams);
  }

  public getStandardGameInstanceReportData(gameInstanceP: IGameInstanceShared): Observable<IGameInstanceSummaryReport> {
    return this.apiService.MakeGetRequest<IGameInstanceSummaryReport>(`report/game-instance-report/for-game/${gameInstanceP.GameId}/by-id/${gameInstanceP.Id}`).pipe(map(res => res));
  }

  public getMultipleStandardGameInstanceReports(dateRangeDataP: IStandardGameReportPerDateRange): Observable<IGameInstanceSummaryReport[]> {
    let queryParams: HttpParams = new HttpParams();
    queryParams = queryParams.append("GameId", dateRangeDataP.GameId);
    queryParams = queryParams.append("StartDate", reportDay(dateRangeDataP.StartDate));
    queryParams = queryParams.append("EndDate", reportDay(dateRangeDataP.EndDate));
    return this.apiService.MakeGetRequest<IGameInstanceSummaryReport[]>(`report/game-instance-report/by-date-range`, queryParams).pipe(map(res => res));
  }

  public getRafflingReportData(gameInstanceP: IGameInstanceShared): Observable<IGetRaffleReportResult> {
    return this.apiService.MakeGetRequest<IGetRaffleReportResult>(`game-instance/players-with-tickets/game/${gameInstanceP.GameId}/${gameInstanceP.Id}`);
  }

  public getPlayedVsNotPlayedTicketsForDateRange(gameIdp: string, startDateP: string, endDateP: string): Observable<IPlayedAndNotPlayedPerInstance[]> {
    return this.apiService.MakeGetRequest<IPlayedAndNotPlayedPerInstance[]>(`report/played-vs-not-played/${gameIdp}/${reportDay(startDateP)}/${reportDay(endDateP)}`);
  }

  public getPlayersPerInstanceForDateRange(gameIdp: string, startDateP: string, endDateP: string): Observable<IPlayersPerDateRange[]> {
    return this.apiService.MakeGetRequest<IPlayersPerDateRange[]>(`report/players-per-instance/${gameIdp}/${reportDay(startDateP)}/${reportDay(endDateP)}`);
  }

  public getComprehensiveGameReportData(gameIdP: string, startDateP: string, endDateP: string): Observable<IComprehensiveReport> {
    return this.apiService.MakeGetRequest<IComprehensiveReport>(`report/comprehensive-report/${gameIdP}/${reportDay(startDateP)}/${reportDay(endDateP)}`);
  }

  public getLotteryLicenseReport(gameIdP: string, lotteryLicenseIdP: string, startDateP: string, endDateP: string): Observable<ILotteryLicenseReport> {
    return this.apiService.MakeGetRequest<ILotteryLicenseReport>(`report/lottery-license-report/${gameIdP}/${lotteryLicenseIdP}/${reportDay(startDateP)}/${reportDay(endDateP)}`);
  }

  public fetchSalesPerGameReport(gameIdP: string): Observable<IPlayerRetentionReportResult[]> {
    return this.apiService.MakeGetRequest<IPlayerRetentionReportResult[]>(`report/sales-per-game/${gameIdP}`);
  }

  public fetchGameCharitySupporterBreakdown(gameIdP: string): Observable<IGameCharitySupporterBreakdown[]> {
    return this.apiService.MakeGetRequest<IGameCharitySupporterBreakdown[]>(`report/game-charity-supporters-breakdown/${gameIdP}`);
  }

  public hourlyTicketSalesBreakdown(gameIdP: string): Observable<IHourlyTicketSalesPerHourPerGame[]> {
    return this.apiService.MakeGetRequest<IHourlyTicketSalesPerHourPerGame[]>(`report/hourly-ticket-sales/${gameIdP}`);
  }

  public getManualVsAutoPlayTickets(gameIdP: string, gameInstanceIdP: string): Observable<IManualAndAutoPlayTickets> {
    return this.apiService.MakeGetRequest<IManualAndAutoPlayTickets>(`report/manual-vs-autoplay-tickets/${gameInstanceIdP}/${gameIdP}`);
  }

  public salesPerDay(gameIdP: string, gameInstanceIdP: string): Observable<ISalesPerDay[]> {
    return this.apiService.MakeGetRequest<ISalesPerDay[]>(`report/sales-per-day/${gameInstanceIdP}/${gameIdP}`);
  }

  public topTenCharityContributors(gameIdP: string, gameInstanceIdP: string): Observable<ICharitySalesPerDraw[]> {
    return this.apiService.MakeGetRequest<ICharitySalesPerDraw[]>(`report/top-charity-contributors/${gameInstanceIdP}/${gameIdP}`);
  }

  public getSalesSummaryByPricePointReport(gameIdP: string, startDateP: string, endDateP: string): Observable<any> {
    return this.apiService.MakeGetRequest<any>(`report/sales-summary-by-price-point/${gameIdP}/${reportDay(startDateP)}/${reportDay(endDateP)}`);
  }

  public getPayoutReport(params: IPayoutReportParams): Observable<IPayoutReportResponse> {
    let queryParams: HttpParams = new HttpParams();
    queryParams = queryParams.append("GameId", params.gameId);
    queryParams = queryParams.append("IncludeWinnerPayouts", params.includeWinnerPayouts.toString());
    queryParams = queryParams.append("IncludeCharityPayouts", params.includeCharityPayouts.toString());
    queryParams = queryParams.append("UseDateRange", params.useDateRange.toString());
    queryParams = queryParams.append("StartDate", reportDay(params.startDate));

    if (params.endDate) {
      queryParams = queryParams.append("EndDate", reportDay(params.endDate));
    }

    queryParams = queryParams.append("CharityPayoutVoidedFilter", params.charityPayoutVoidedFilter);
    queryParams = queryParams.append("WinnerPayoutStatusFilter", params.winnerPayoutStatusFilter);
    queryParams = queryParams.append("DateFilterType", params.dateFilterType);

    return this.apiService.MakeGetRequest<IPayoutReportResponse>(`report/payout-report`, queryParams).pipe(map(res => res));
  }
}
